/**
 * Comprovante — tela de sucesso pós-envio.
 *
 * Gera um JPG em retrato (largura fixa 540px @scale=2 → 1080px de largura):
 * a ALTURA acompanha o conteúdo — cresce conforme o número de peças e encolhe
 * quando são poucas (sem faixa branca). Capturado fora da tela pelo html2canvas.
 * Na tela o colaborador vê só a PRÉVIA da imagem + compartilhar (Web Share API
 * com arquivo; fallback download). A renderização do card + captura vivem em
 * comprovanteRender.jsx (compartilhadas com os comprovantes de lote da Gestão).
 */
import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { FiShare2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { PageContainer } from "../../../styles/shared";
import {
  ComprovanteCardView, baixarBlob, jpgDeElemento,
} from "./comprovanteRender";
import {
  AcoesColuna, AcaoLeveBtn, CompOffscreen, PrevisualizacaoImg,
} from "./styles";

export default function Comprovante() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { usuario } = useAuth();
  const toast = useToast();

  const cardRef = useRef(null);
  const [blobJpg, setBlobJpg] = useState(null);
  const [previaUrl, setPreviaUrl] = useState("");
  const [gerando, setGerando] = useState(true);

  const solicitacao = state?.solicitacao;
  const familia = state?.familia;
  // Reaberto pelo detalhe (não é a tela de sucesso pós-envio): ajusta os textos
  // e o "voltar" para retornar ao detalhe de onde veio.
  const reView = Boolean(state?.reView);

  useEffect(() => {
    if (!solicitacao || !cardRef.current) return undefined;
    let cancelado = false;
    (async () => {
      try {
        const blob = await jpgDeElemento(cardRef.current);
        if (cancelado) return;
        setBlobJpg(blob);
        setPreviaUrl(URL.createObjectURL(blob));
      } catch {
        if (!cancelado) {
          toast.error("Não foi possível gerar a imagem do comprovante.");
        }
      } finally {
        if (!cancelado) setGerando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
    // Gera uma única vez, quando o card já está no DOM.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitacao?.id]);

  useEffect(
    () => () => {
      if (previaUrl) URL.revokeObjectURL(previaUrl);
    },
    [previaUrl],
  );

  if (!solicitacao || !familia) {
    return <Navigate to=".." replace />;
  }

  const compartilhar = async () => {
    if (!blobJpg) throw new Error("O comprovante ainda está sendo gerado.");
    const arquivo = new File([blobJpg], `solicitacao-${solicitacao.numero}.jpg`, {
      type: "image/jpeg",
    });
    const dadosShare = {
      files: [arquivo],
      title: `Solicitação ${solicitacao.numero}`,
      text: `Solicitação ${solicitacao.numero} (${solicitacao.tipo}) enviada — aguardando autorização.`,
    };
    if (navigator.canShare?.(dadosShare)) {
      try {
        await navigator.share(dadosShare);
      } catch (e) {
        // Cancelar o menu de compartilhar não é erro.
        if (e?.name !== "AbortError") throw e;
      }
      return;
    }
    baixarBlob(blobJpg, arquivo.name);
    toast.info(
      "Este navegador não permite compartilhar arquivos — o comprovante foi baixado no aparelho.",
    );
  };

  return (
    <PageContainer>
      <EntityHeader
        onBack={() => (reView ? navigate(-1) : navigate(".."))}
        title={reView ? "Comprovante" : "Solicitação enviada"}
        subtitle={
          reView
            ? "Baixe ou compartilhe novamente"
            : "Comprovante gerado — compartilhe com o supervisor"
        }
      />

      {/* Card do story renderizado fora da tela, só para captura. */}
      <CompOffscreen aria-hidden="true">
        <ComprovanteCardView
          solicitacao={solicitacao}
          familia={familia}
          solicitanteFallback={usuario?.nome}
          cardRef={cardRef}
        />
      </CompOffscreen>

      {gerando && (
        <div style={{ color: "var(--ink-2)", fontSize: 13 }}>
          Gerando imagem do comprovante…
        </div>
      )}
      {previaUrl && (
        <PrevisualizacaoImg
          src={previaUrl}
          alt={`Comprovante da solicitação ${solicitacao.numero}`}
        />
      )}

      <AcoesColuna>
        <AsyncButton
          onClick={compartilhar}
          disabled={!blobJpg}
          errorMessage="Não foi possível compartilhar o comprovante."
          style={{ width: "100%", minHeight: 52, fontSize: 16, justifyContent: "center" }}
        >
          <FiShare2 size={18} style={{ marginRight: 6 }} /> Compartilhar comprovante
        </AsyncButton>
        <AcaoLeveBtn
          type="button"
          style={{ justifyContent: "center" }}
          onClick={() => navigate(`../${familia}/${solicitacao.id}`)}
        >
          Ver solicitação
        </AcaoLeveBtn>
        <AcaoLeveBtn
          type="button"
          style={{ justifyContent: "center" }}
          onClick={() => navigate("..")}
        >
          Nova solicitação
        </AcaoLeveBtn>
      </AcoesColuna>
    </PageContainer>
  );
}
